import { FC } from "react"
import { ShieldAlert } from "lucide-react"
import ApproveDomainButton from "../approve-domain-button"
import { formatDateTimeFromTimestamp } from "../../utils"
import { usePendingEmbedDomains } from "../../hooks"

type Props = {
  integrationId?: string
}

/**
 * The blocked domains for one integration, each approvable in one click.
 *
 * This is the job the Activity Log below it could never really do: the log is
 * a flat, paged list of every request, where a rejected referrer is one row
 * among hundreds and approving it meant copying the URL into the Approved URLs
 * modal and remembering to append the wildcard.
 *
 * Renders nothing when this integration has no blocked domains.
 */
const BlockedDomainsCard: FC<Props> = ({ integrationId }) => {
  const { domains, approve, approvingHost } = usePendingEmbedDomains({
    enabled: !!integrationId,
    integrationId
  })

  if (domains.length === 0) return null

  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-6 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <ShieldAlert size={15} className="text-amber-500" aria-hidden="true" />
        <span className="text-sm font-medium text-amber-900">
          {domains.length === 1
            ? "1 website is blocked from loading this embed"
            : `${domains.length} websites are blocked from loading this embed`}
        </span>
      </div>
      <p className="mb-4 text-sm text-amber-800">
        These URLs are not on your approved list, so the embed will not load
        there. Approving a domain allows every page on it.
      </p>

      <ul className="space-y-2">
        {domains.map(domain => (
          <li
            key={domain.host}
            className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white px-3 py-2.5"
          >
            <div className="min-w-0">
              <span className="block truncate text-sm font-medium text-base-dark">
                {domain.host}
              </span>
              <span className="block truncate text-xs text-neutral-500">
                {domain.hits === 1 ? "1 attempt" : `${domain.hits} attempts`},
                last{" "}
                {formatDateTimeFromTimestamp(
                  domain.last_seen,
                  "LL/dd/yyyy h:mm a"
                )}{" "}
                &middot; approves {domain.suggested_pattern}
              </span>
            </div>
            <ApproveDomainButton
              domain={domain}
              approve={approve}
              approvingHost={approvingHost}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default BlockedDomainsCard
